import { fmtBRL, PAY_LABELS } from "../lib/constants";
import api from "../lib/api";

// Escape HTML entities to prevent injection when we render order data
// (client-provided) into the print window's markup.
function esc(value) {
  if (value === null || value === undefined) return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Default label configuration — sizes are in pt (thermal 40x40mm printer)
export const DEFAULT_LABEL_CONFIG = {
  headerSize: 6.5,       // OS number + date row
  clientSize: 9,         // Client name (biggest, reference)
  deviceSize: 8,         // model + color
  sectionLabelSize: 6,   // "Serviços", "Problema", "Obs. interna"
  servicesSize: 8,       // Each service line
  noteSize: 7,           // Problem + observation notes
  imeiSize: 6,           // IMEI footer
  showProblem: true,
  showObservation: true,
  showIMEI: true,
};

let cachedLabelConfig = null;
async function loadLabelConfig() {
  if (cachedLabelConfig) return cachedLabelConfig;
  try {
    const r = await api.get("/settings");
    const label = r.data?.label || {};
    cachedLabelConfig = { ...DEFAULT_LABEL_CONFIG, ...label };
  } catch {
    cachedLabelConfig = { ...DEFAULT_LABEL_CONFIG };
  }
  return cachedLabelConfig;
}

// Called by SettingsView after saving new label config
export function invalidateLabelConfigCache() {
  cachedLabelConfig = null;
}

export async function printOrderLabel(order) {
  const cfg = await loadLabelConfig();
  const win = window.open("", "_blank", "width=400,height=300");
  if (!win) return;

  // Enriquece o nome do serviço com sub-opções (mensagem / %)
  const buildServiceLine = (name) => {
    const extras = [];
    if (name === "Troca de tela" && order.telaMsg) {
      extras.push(order.telaMsg === "com" ? "com mensagem" : "sem mensagem");
    }
    if (name === "Troca de bateria") {
      if (order.batteryMsg) extras.push(order.batteryMsg === "com" ? "com mensagem" : "sem mensagem");
      if (order.batteryPercent) extras.push(`${order.batteryPercent}%`);
    }
    if (name === "Subir bateria" && order.batteryPercent) {
      extras.push(`${order.batteryPercent}%`);
    }
    const safeName = esc(name);
    if (!extras.length) return `<li>• ${safeName}</li>`;
    return `<li>• ${safeName} <span class="tag">(${esc(extras.join(" · "))})</span></li>`;
  };

  const services = (order.services || []).map(buildServiceLine).join("") || "<li>—</li>";
  const clientName = esc(order.clientName || "");
  const orderNumber = esc(order.orderNumber || "");
  const entryDate = esc(order.entryDate || "");
  const entryTime = esc(order.entryTime || "");
  const model = esc(order.model || "");
  const color = esc(order.color || "");
  const imeiTail = esc((order.imei || "").slice(-4));
  const problem = esc(order.problem || "");
  const observation = esc(order.observation || "");

  const html = `
    <html>
      <head>
        <meta charset="utf-8" />
        <title>Etiqueta ${orderNumber}</title>
        <style>
          * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          html, body { margin: 0; padding: 0; }
          body {
            font-family: -apple-system, "Segoe UI", "Helvetica Neue", sans-serif;
            color: #0f172a;
            padding: 2mm;
          }
          .label {
            width: 40mm;
            height: 40mm;
            border: 1.2px solid #1e3a8a;
            border-radius: 1.5mm;
            padding: 1.5mm 2mm;
            background: #fff;
            overflow: hidden;
            display: flex; flex-direction: column;
          }
          .top {
            display: flex; justify-content: space-between; align-items: center;
            border-bottom: 1px dashed #1e3a8a;
            padding-bottom: 0.6mm; margin-bottom: 0.8mm;
            font-size: ${cfg.headerSize}pt;
          }
          .os {
            font-weight: 700; font-size: ${cfg.headerSize}pt; color: #1e3a8a;
            text-transform: uppercase; letter-spacing: 0.02em;
          }
          .date { color: #475569; font-variant-numeric: tabular-nums; }
          .client {
            font-size: ${cfg.clientSize}pt; font-weight: 800; color: #0f172a;
            line-height: 1.02; text-transform: uppercase; letter-spacing: -0.01em;
            margin-bottom: 0.6mm; word-break: break-word;
            display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden;
          }
          .device {
            font-size: ${cfg.deviceSize}pt; color: #334155; font-weight: 700;
            margin-bottom: 0.8mm;
          }
          .section-label {
            font-size: ${cfg.sectionLabelSize}pt; color: #64748b;
            text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700;
            margin-top: 0.6mm; margin-bottom: 0.2mm;
          }
          ul { margin: 0; padding: 0; list-style: none; }
          li { font-size: ${cfg.servicesSize}pt; font-weight: 700; color: #0f172a; line-height: 1.15; }
          li .tag {
            font-size: ${Math.max(cfg.servicesSize - 1.5, 5)}pt; font-weight: 600; color: #6b21a8;
            background: #f3e8ff; padding: 0 0.6mm; border-radius: 0.5mm; margin-left: 0.3mm;
          }
          .note {
            font-size: ${cfg.noteSize}pt; color: #0f172a; line-height: 1.15;
            background: #fef9c3; border-left: 1px solid #ca8a04;
            padding: 0.4mm 0.8mm; border-radius: 0.4mm; margin-top: 0.3mm;
            display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
          }
          .note.obs { background: #eff6ff; border-left-color: #2563eb; color: #1e3a8a; }
          .imei { font-size: ${cfg.imeiSize}pt; color: #64748b; margin-top: auto; padding-top: 0.5mm; font-variant-numeric: tabular-nums; }
          @media print {
            body { padding: 0; }
            @page { size: 40mm 40mm; margin: 0; }
          }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="top">
            <span class="os">${orderNumber}</span>
            <span class="date">${entryDate} ${entryTime}</span>
          </div>
          <div class="client">${clientName || "—"}</div>
          <div class="device">${model}${color ? ` · ${color}` : ""}</div>
          <div class="section-label">Serviços</div>
          <ul>${services}</ul>
          ${cfg.showProblem && problem ? `<div class="section-label">Problema</div><div class="note">${problem}</div>` : ""}
          ${cfg.showObservation && observation ? `<div class="section-label">Obs. interna</div><div class="note obs">${observation}</div>` : ""}
          ${cfg.showIMEI && imeiTail ? `<div class="imei">IMEI ****${imeiTail}</div>` : ""}
        </div>
      </body>
    </html>
  `;

  win.document.open();
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    win.focus();
    win.print();
  };
}

export function printOrderOS(order) {
  const win = window.open("", "_blank", "width=820,height=1000");
  if (!win) return;

  const itemRows = (order.items || []).length
    ? (order.items || [])
        .map(
          (it) => `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0">${esc(it.name)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:center">${esc(it.qty)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:right">R$ ${fmtBRL(it.unitValue)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:right">R$ ${fmtBRL(
          (it.qty || 1) * (it.unitValue || 0)
        )}</td>
      </tr>`
        )
        .join("")
    : `
      <tr>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0">${esc(
          (order.services || []).join(", ") || "Serviço"
        )}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:center">1</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:right">R$ ${fmtBRL(order.total)}</td>
        <td style="padding:6px 8px;border-bottom:1px solid #e2e8f0;text-align:right">R$ ${fmtBRL(order.total)}</td>
      </tr>`;

  const items = itemRows;

  const services = (order.services || []).map(esc).join(", ") || "—";
  const payLine = order.payMethod ? esc(PAY_LABELS[order.payMethod]) : "—";

  const logoUrl = `${window.location.origin}/logo-sm.png`;

  const html = `
    <html>
      <head>
        <title>OS ${esc(order.orderNumber || "")} — ${esc(order.clientName || "")}</title>
        <style>
          * { box-sizing: border-box; }
          body { font-family: -apple-system, "Segoe UI", sans-serif; color: #0f172a; margin: 0; padding: 24px; }
          .head { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #1e3a8a; padding-bottom: 12px; margin-bottom: 20px; }
          .head img { height: 56px; width: auto; }
          .subtitle { color: #64748b; font-size: 12px; letter-spacing: 0.05em; text-transform: uppercase; margin-top: 4px; }
          .os-num { text-align: right; font-size: 12px; color: #475569; }
          .os-num b { color: #1e3a8a; font-size: 18px; display: block; }
          h3 { margin: 16px 0 6px; font-size: 11px; color: #64748b; letter-spacing: 0.1em; text-transform: uppercase; }
          .box { border: 1px solid #e2e8f0; border-radius: 8px; padding: 10px 14px; background: #f8fafc; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; margin-top: 6px; }
          th { text-align: left; background: #1e3a8a; color: #fff; padding: 7px 8px; font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
          .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; font-size: 13px; }
          .grid div span { color: #64748b; font-size: 11px; display: block; }
          .total { margin-top: 14px; text-align: right; font-size: 18px; font-weight: 800; color: #1e3a8a; }
          .foot { margin-top: 32px; font-size: 11px; color: #64748b; border-top: 1px dashed #cbd5e1; padding-top: 14px; }
          .sign { margin-top: 40px; display: flex; justify-content: space-around; font-size: 11px; color: #64748b; }
          .sign div { text-align: center; }
          .sign span { display: block; border-top: 1px solid #94a3b8; padding-top: 4px; width: 200px; margin-top: 26px; }
          @media print { body { padding: 12mm; } }
        </style>
      </head>
      <body>
        <div class="head">
          <div>
            <img src="${esc(logoUrl)}" alt="Phone Lab" />
            <div class="subtitle">Ordem de Serviço</div>
          </div>
          <div class="os-num">
            <b>${esc(order.orderNumber || "")}</b>
            ${esc(order.entryDate || "")} · ${esc(order.entryTime || "")}
          </div>
        </div>

        <h3>Cliente</h3>
        <div class="box grid">
          <div><span>Nome</span><b>${esc(order.clientName || "")}</b></div>
          <div><span>Telefone</span>${esc(order.clientPhone || "")}</div>
        </div>

        <h3>Aparelho</h3>
        <div class="box grid">
          <div><span>Marca / Modelo</span><b>${esc(order.brand || "")} ${esc(order.model || "")}</b></div>
          <div><span>Cor</span>${esc(order.color || "—")}</div>
          <div><span>IMEI</span>${esc(order.imei || "—")}</div>
          <div><span>Serviço(s)</span>${services}</div>
        </div>

        <h3>Problema / Observações</h3>
        <div class="box">${esc(order.problem || "—")}${
          order.observation ? "<br><i>" + esc(order.observation) + "</i>" : ""
        }</div>

        <h3>Itens</h3>
        <table>
          <thead><tr><th>Descrição</th><th style="text-align:center">Qtd</th><th style="text-align:right">Valor Unit.</th><th style="text-align:right">Total</th></tr></thead>
          <tbody>${items || `<tr><td colspan="4" style="padding:12px;text-align:center;color:#94a3b8">Sem itens detalhados</td></tr>`}</tbody>
        </table>

        <div class="total">TOTAL: R$ ${fmtBRL(order.total)}</div>
        <div style="text-align:right;font-size:12px;color:#475569">Forma de pagamento: <b>${payLine}</b></div>

        <div class="foot">
          Ao assinar, o cliente declara ciência do serviço a ser executado e das condições de garantia.<br>
          Garantia de 90 dias para o serviço prestado, conforme código do consumidor.
        </div>

        <div class="sign">
          <div><span>Cliente</span></div>
          <div><span>Assistência Técnica</span></div>
        </div>
      </body>
    </html>
  `;

  // Build the document safely via the DOM API. All order-owned strings are
  // HTML-escaped above (via `esc`) so no user input can inject markup.
  win.document.open();
  win.document.write(html);
  win.document.close();
  // Fire the print dialog once the popup finished parsing.
  win.onload = () => {
    win.focus();
    win.print();
  };
}
